import { useEffect, useRef, type JSX } from 'react'
import { cn } from '@ttab/elephant-ui/utils'
import type { ImageSearchHit } from '../lib/types'


export const Thumbnail = ({ hit, isHighlighted, onOpen }: {
  hit: ImageSearchHit
  isHighlighted: boolean
  onOpen: (hit: ImageSearchHit) => void
}): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  // Follow the preview through the grid
  useEffect(() => {
    if (isHighlighted) {
      containerRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [isHighlighted])

  return (
    <div
      ref={containerRef}
      data-highlighted={isHighlighted || undefined}
      className={cn(
        'flex place-content-center bg-gray-200 dark:bg-table-focused min-h-[144px] rounded-sm overflow-hidden cursor-pointer',
        'relative transition-shadow duration-150',
        // Ring and shadow don't affect layout, so neighbours stay put
        isHighlighted && 'z-10 shadow-lg ring-4 ring-[color:CanvasText] '
        + 'ring-offset-[0.5px] ring-offset-background'
      )}
      onClick={() => onOpen(hit)}
    >
      <img
        ref={imageRef}
        src={hit.thumbnailUrl}
        title={hit.description}
        className='max-h-[176px] object-contain m-width-auto'
        onDragStartCapture={(e) => {
          e.stopPropagation()

          if (!imageRef.current) {
            return
          }

          // Create cloned element to force as drag image
          const el = imageRef.current
          const clone = el.cloneNode(true) as HTMLImageElement
          clone.dataset.dragClone = ''
          const { left, top } = el.getBoundingClientRect()

          clone.style.width = `${el.offsetWidth}px`
          clone.style.height = `${el.offsetHeight}px`
          clone.style.background = `url('${hit.thumbnailUrl}')`

          el.style.opacity = '0.5'
          e.dataTransfer.clearData()

          const image = {
            uri: hit.uri,
            rel: hit.linkRel,
            type: hit.linkType,
            byline: hit.byline,
            text: hit.description,
            href: hit.originalUrl,
            proxy: hit.previewUrl,
            width: hit.hiresWidth,
            height: hit.hiresHeight
          }

          e.dataTransfer.setData(hit.dragMimeType, JSON.stringify(image))
          e.dataTransfer.setDragImage(
            clone,
            (e.clientX - left) * 0.2,
            (e.clientY - top) * 0.2
          )
          document.body.appendChild(clone)
        }}
        onDragEndCapture={() => {
          const el = imageRef.current
          if (el) {
            el.style.opacity = '1'
          }
          document.querySelectorAll('img[data-drag-clone]')
            .forEach((clone) => clone.remove())
        }}
      />
    </div>
  )
}
